import { Alert, Badge, Button, ToggleContainer } from '@root/components'
import { useElsterCertificateStatus } from '@root/query/hooks'
import { formatDate } from '@root/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FaFileAlt, FaUpload } from 'react-icons/fa'
import styled from 'styled-components'
import ElsterSettingsForm from './ElsterSettingsForm'
import { ElsterToggleTitle } from './ElsterToggleTitle'

const ElsterCertificateCard: React.FC = () => {
  const [t] = useTranslation()
  const { data } = useElsterCertificateStatus()
  const [isReplacing, setIsReplacing] = React.useState(false)

  const hasCertificate = Boolean(data?.has_certificate)
  const hasValidCertificate = Boolean(data?.has_certificate && data?.is_valid)
  const [isOpen, setIsOpen] = React.useState(!hasValidCertificate)

  React.useEffect(() => {
    if (!hasValidCertificate) {
      setIsOpen(true)
    }
  }, [hasValidCertificate])

  const statusTone = hasValidCertificate ? 'success' : hasCertificate ? 'error' : 'warning'

  return (
    <ToggleContainer
      title={<ElsterToggleTitle label={t('settings.elsterSettings.formTitle')} tone={statusTone} />}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <Content>
        {hasCertificate && !hasValidCertificate && (
          <Alert
            title={t('settings.elsterSettings.alertInvalid.title')}
            description={t('settings.elsterSettings.alertInvalid.description')}
            type="error"
            marginBottom={0}
          />
        )}
        {hasValidCertificate && (
          <Info>{t('settings.elsterSettings.alertSuccess.description')}</Info>
        )}
        {!hasCertificate && <Info>{t('settings.elsterSettings.nextStep')}</Info>}
        {hasCertificate && (
          <CertificateRow>
            <FaFileAlt />
            <CertificateInfo>
              <CertificateName>
                {data?.label || t('settings.elsterSettings.certificate.defaultName')}
              </CertificateName>
              {data?.updated_at && (
                <CertificateMeta>
                  {t('settings.elsterSettings.certificate.addedAt', {
                    date: formatDate(data.updated_at)
                  })}
                </CertificateMeta>
              )}
            </CertificateInfo>
            <Badge type={data?.is_valid ? 'success' : 'error'}>
              {data?.is_valid
                ? t('settings.elsterSettings.certificate.valid')
                : t('settings.elsterSettings.certificate.invalid')}
            </Badge>
          </CertificateRow>
        )}

        {hasCertificate && !isReplacing && (
          <ReplaceRow>
            <ReplaceInfo>{t('settings.elsterSettings.certificate.renewInfo')}</ReplaceInfo>
            <Button
              type="button"
              isSecondary
              icon={<FaUpload />}
              onClick={() => setIsReplacing(true)}
            >
              {t('settings.elsterSettings.certificate.replace')}
            </Button>
          </ReplaceRow>
        )}

        {(!hasCertificate || isReplacing) && (
          <ElsterSettingsForm
            onUploaded={() => {
              setIsReplacing(false)
              setIsOpen(false)
            }}
          />
        )}
      </Content>
    </ToggleContainer>
  )
}

export default ElsterCertificateCard

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const CertificateRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 1px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  background: ${({ theme }) => theme.colors.defaultHover};

  > svg {
    color: ${({ theme }) => theme.colors.iconGray};
    width: ${({ theme }) => theme.fontSize.xl}rem;
    height: ${({ theme }) => theme.fontSize.xl}rem;
    flex: 0 0 auto;
  }
`

const CertificateInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const CertificateName = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutralBlack};
  overflow-wrap: anywhere;
`

const CertificateMeta = styled.div`
  margin-top: ${({ theme }) => theme.spacing.xxs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const ReplaceRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const ReplaceInfo = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const Info = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.iconGray};
  line-height: 1.4;
`
